import React, {useMemo, useRef} from "react";
import {useFrame} from "@react-three/fiber";
import * as THREE from "three";
import {GUIDING_AXIS_COLORS} from "./guidingAxisColors";
import {getRotationAxisDirInPoseParent} from "./freeRotationDrag";
import {normalizeLocalRotationAngles} from "./localRotation";
import {getInstanceLocalCornersM} from "./snapPlacement";

const UP = new THREE.Vector3(0, 1, 0);

const alignCylinderAlongDirection = (mesh, direction, halfLength) => {
    const dir = direction.clone().normalize();
    mesh.quaternion.setFromUnitVectors(UP, dir);
    mesh.position.copy(dir.multiplyScalar(halfLength));
};

/**
 * Линия оси вращения через панель (в pose-группе). При перетаскивании направление фиксируется.
 */
const PanelRotationAxisIndicator = ({
    inst,
    orientationSourceRef,
    referenceAxes,
    localRotationAngles,
    liveLocalAnglesRef = null,
    rotationAxis = "z",
    frozenDirInPose = null,
}) => {
    const shaftRef = useRef(null);
    const axisColor = GUIDING_AXIS_COLORS[rotationAxis] || "#000000";

    const {radius, minSpan} = useMemo(() => {
        const corners = getInstanceLocalCornersM(inst);
        let minSpanLocal = Infinity;
        for (let i = 0; i < corners.length; i += 1) {
            for (let j = i + 1; j < corners.length; j += 1) {
                const dx = corners[i][0] - corners[j][0];
                const dy = corners[i][1] - corners[j][1];
                const dz = corners[i][2] - corners[j][2];
                const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
                if (d > 1e-6) minSpanLocal = Math.min(minSpanLocal, d);
            }
        }
        const span = Number.isFinite(minSpanLocal) ? minSpanLocal : 0.2;
        return {
            radius: Math.max(0.0025, Math.min(0.006, span * 0.012)),
            minSpan: span,
        };
    }, [inst]);

    useFrame(() => {
        const orient = orientationSourceRef?.current;
        const shaft = shaftRef.current;
        if (!orient || !shaft) return;

        const dirPose = new THREE.Vector3();
        const angles = normalizeLocalRotationAngles(
            liveLocalAnglesRef?.current ?? localRotationAngles,
        );

        if (frozenDirInPose?.current) {
            dirPose.copy(frozenDirInPose.current);
        } else {
            dirPose.copy(getRotationAxisDirInPoseParent(
                referenceAxes,
                angles,
                rotationAxis,
                orient,
            ));
        }

        const corners = getInstanceLocalCornersM(inst);
        const axis = dirPose.clone().normalize();
        let minProj = Infinity;
        let maxProj = -Infinity;
        corners.forEach((corner) => {
            const v = new THREE.Vector3(
                Number(corner[0]) || 0,
                Number(corner[1]) || 0,
                Number(corner[2]) || 0,
            ).applyQuaternion(orient.quaternion);
            const p = v.dot(axis);
            minProj = Math.min(minProj, p);
            maxProj = Math.max(maxProj, p);
        });

        if (!Number.isFinite(minProj) || !Number.isFinite(maxProj)) {
            minProj = -minSpan / 2;
            maxProj = minSpan / 2;
        }

        const padding = Math.max(0.02, minSpan * 0.08);
        const length = Math.max(minSpan * 0.5, maxProj - minProj + padding * 2);
        const mid = (minProj + maxProj) / 2;

        shaft.scale.set(1, length, 1);
        alignCylinderAlongDirection(shaft, axis, mid);
    });

    return (
        <group userData={{module3dRole: "helper"}}>
            <mesh ref={shaftRef} raycast={() => null} renderOrder={20}>
                <cylinderGeometry args={[radius, radius, 1, 12]}/>
                <meshBasicMaterial
                    color={axisColor}
                    depthTest={false}
                    depthWrite={false}
                    transparent
                    opacity={0.95}
                />
            </mesh>
        </group>
    );
};

export default PanelRotationAxisIndicator;
